import {
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  useTheme,
  Box,
} from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Vector3 } from "three";
import {
  EDisplayType,
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import {
  IMeasurementHandler,
  MeasurementType,
} from "../../../viewer/measurement/types";
import ProjectVolumeCalcMenu from "./ProjectVolumeCalcMenu";
import { VolumeCalcColumn } from "../BottomDrawer/CalcDrawerContent";
import CalcContext from "../Contexts/CalcContext";
import { Q3DDataTable, Q3DDataTableHeaderType } from "../../Parts/Q3DDataTable";

interface ICalResProps {
  selectedItem: IMeasurementHandler;
  handleClickCancel: () => void;
  showHeatMap: boolean;
  rowsVolumeCalc: VolumeCalcColumn[];
  setRowsVolumeCalc: Dispatch<SetStateAction<VolumeCalcColumn[]>>;
  // editType: string;
  // setEditType: Dispatch<SetStateAction<string>>;
  startEdit: () => void;
  finishEdit: () => void;
  // EditType: { name: string }[];
}

const CalcResultDetail = (calcResProps: ICalResProps) => {
  const { viewer, currentViewerMode, projectDetail, displayType } =
    useContext(ProjectDetailContext);
  const { onEditingMode } = useContext(CalcContext);

  /*****************************************
      state
    ******************************************/

  const { t, i18n } = useTranslation();

  /*****************************************
      Effect
    ******************************************/
  // useEffect(() => {
  //   if (currentViewerMode === EViewerMode.CALCULATION)
  //     calcResProps.setEditType(calcResProps.EditType[0].name);
  // }, [currentViewerMode]);

  useEffect(() => {
    if (onEditingMode) calcResProps.startEdit();

    return () => {
      //   calcResProps.finishEdit();
    };
  }, [onEditingMode]);

  useEffect(() => {
    const offset = viewer.getOffset();
    const pointsList = calcResProps.selectedItem?._points.map(
      (data: any, index: any) => {
        return {
          id: index,
          pointName: index + 1,
          x: data.x - offset.x,
          y: data.y - offset.y,
          z: data.z - offset.z,
        };
      }
    ) as VolumeCalcColumn[];
    calcResProps.setRowsVolumeCalc(pointsList);

    return () => {
      calcResProps.setRowsVolumeCalc([]);
    };
  }, [calcResProps.selectedItem]);

  /*****************************************
      function
    ******************************************/
  const selectedItemData = () => {
    return calcResProps.selectedItem?.type === MeasurementType.TWO_POINT ? (
      <div>
        <Typography
          padding={"0px 20px 0px 10px"}
          variant="body2"
          gutterBottom
          style={customColor}
        >
          {t("HORIZONTAL")}
          <span style={{ float: "right" }}>
            {Math.sqrt(
              (calcResProps.selectedItem._points[0].x -
                calcResProps.selectedItem._points[1].x) **
                2 +
                (calcResProps.selectedItem._points[0].y -
                  calcResProps.selectedItem._points[1].y) **
                  2
            ).toFixed(3)}{" "}
            m
          </span>
        </Typography>
        <Typography
          padding={"0px 20px 0px 10px"}
          variant="body2"
          gutterBottom
          style={customColor}
        >
          {t("VERTICAL")}
          <span style={{ float: "right" }}>
            {Math.abs(
              calcResProps.selectedItem._points[0].z -
                calcResProps.selectedItem._points[1].z
            ).toFixed(3)}{" "}
            m
          </span>
        </Typography>
      </div>
    ) : (
      <div></div>
    );
  };

  const resCalcType = (data: any) => {
    if (!data) return;
    const type: string = data.type;
    if (type === MeasurementType.VOLUME_CALCULATION) {
      return t("VOLUME_CALC");
    } else if (type === MeasurementType.TWO_POINT) {
      return t("DISTANCE_TWO_POINTS");
    } else if (type === MeasurementType.MULTIPLE_POINT) {
      return t("DISTANCE_MULTIPLE_POINTS");
    } else if (type === MeasurementType.VERTICAL) {
      return t("VERTICAL_DISTANCE");
    } else if (type === MeasurementType.HORIZONTAL) {
      return t("HORIZONTAL_DISTANCE");
    } else if (type === MeasurementType.FLAT_AREA_CALCULATION) {
      return t("FLAT_AREA_CALC");
    } else if (type === MeasurementType.SURFACE) {
      return t("SURFACE_DISTANCE");
    }
  };

  const updateMeasurement = (id: string, key: string, value: any) => {
    const offset = viewer.getOffset();
    const points = calcResProps.rowsVolumeCalc.map((data) => {
      if (id === data.id) {
        switch (key) {
          case "x":
            return new Vector3(Number(value), data.y, data.z).add(offset);
          case "y":
            return new Vector3(data.x, Number(value), data.z).add(offset);
          case "z":
            return new Vector3(data.x, data.y, Number(value)).add(offset);
          default:
            return new Vector3(data.x, data.y, data.z).add(offset);
        }
      } else {
        return new Vector3(data.x, data.y, data.z).add(offset);
      }
    });
    viewer.setPointsToMeasurement(calcResProps.selectedItem.id, points);
    const pointsList = calcResProps.selectedItem._points.map(
      (data: any, index: any) => {
        return {
          id: index,
          pointName: index + 1,
          x: data.x - offset.x,
          y: data.y - offset.y,
          z: data.z - offset.z,
        };
      }
    ) as VolumeCalcColumn[];
    calcResProps.setRowsVolumeCalc(pointsList);
    calcResProps.startEdit();
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customBackground = {
    background: theme.palette.base.modal,
  };

  const headers = [
    {
      key: "pointName",
      name: t("POINT_NAME"),
      type: "text",
    },
    {
      key: "y",
      name: t("X_COORDINATE"),
      type: onEditingMode ? "numberInput" : "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "x",
      name: t("Y_COORDINATE"),
      type: onEditingMode ? "numberInput" : "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "z",
      name: t("Z_COORDINATE"),
      type: onEditingMode ? "numberInput" : "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
  ] as Q3DDataTableHeaderType[];

  const calculateVolumeDifferences = useMemo(() => {
    const { rowsVolumeCalc, selectedItem } = calcResProps;
    if (!rowsVolumeCalc || !selectedItem?.type) return undefined;

    const types = [
      MeasurementType.TWO_POINT,
      MeasurementType.VERTICAL,
      MeasurementType.HORIZONTAL,
      MeasurementType.SURFACE,
    ];

    if (types.includes(selectedItem.type)) {
      if (rowsVolumeCalc.length < 2) return rowsVolumeCalc;

      const [first, second] = rowsVolumeCalc;

      return [
        ...rowsVolumeCalc,
        {
          id: 2,
          pointName: "1-2",
          x: first.x - second.x,
          y: first.y - second.y,
          z: first.z - second.z,
        },
      ];
    }

    return rowsVolumeCalc;
  }, [calcResProps]);

  return (
    <>
      <Q3DDataTable
        headers={headers}
        rows={calculateVolumeDifferences}
        onChange={updateMeasurement}
        ContainerProps={{
          sx: { border: `1px solid ${theme.palette.base.contrast}`, flex: 3 },
        }}
      />
    </>
  );
};

export default CalcResultDetail;
